import { useMemo, useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Button, Modal } from "@fawkes/ui";

/** Een taak om te kiezen, zoals GET /api/taak-keuzes hem geeft. */
export interface TaakKeuze {
  id: number;
  naam: string;
  omschrijving: string | null;
  diersoortId: number | null;
  diersoort: string | null;
  handeling: string;
  standaardTeam: string | null;
  standaardPrioriteit: string;
  wizardDefinitionId: string | null;
  actief: boolean;
}

interface Optie {
  id: string;
  label: string;
}

/** Wat een taak doet als je hem uitvoert, met de naam die de schermen tonen. */
export const HANDELINGEN: { id: string; label: string }[] = [
  { id: "VRIJ", label: "Gewone taak (geen invoerscherm)" },
  { id: "ENTING", label: "Enting" },
  { id: "ONTWORMING", label: "Ontworming" },
  { id: "ONTVLOOIING", label: "Ontvlooiing" },
  { id: "WEGING", label: "Wegen" },
  { id: "VOEDING", label: "Voeding" },
  { id: "CHIPREGISTRATIE", label: "Chipregistratie" },
  { id: "NOTITIE", label: "Notitie" },
  { id: "DOCUMENT", label: "Document" },
  { id: "WIZARD", label: "Wizard" },
];

export function handelingLabel(id: string | null | undefined): string {
  return HANDELINGEN.find((h) => h.id === id)?.label ?? "Gewone taak";
}

/**
 * Taken om te kiezen (Maarten, 2026-10-09): naast de automatische taakregels de taken die een medewerker zelf
 * kiest bij een dier, per diersoort, met de handeling die de taak bij uitvoeren opent. "Enting geven" opent de
 * entingen en is af zodra die enting er staat; zo kan alles via een taak toegevoegd of gecontroleerd worden.
 */
export function TaakKeuzesSection({ teams, prioriteiten, wizards }: { teams: Optie[]; prioriteiten: Optie[]; wizards: Optie[] }) {
  const lijst = useCustom<TaakKeuze[]>({ url: "taak-keuzes", method: "get" });
  const soortenData = useCustom<{ id: number; label: string }[]>({ url: "animals/reference/species", method: "get" });
  const soorten = Array.isArray(soortenData.data?.data) ? soortenData.data!.data : [];
  const keuzes = Array.isArray(lijst.data?.data) ? lijst.data!.data : [];
  const [soort, setSoort] = useState<number | "alle" | "elk">("alle");
  const [bewerk, setBewerk] = useState<TaakKeuze | "nieuw" | null>(null);

  const zichtbaar = useMemo(() => keuzes.filter((k) =>
    soort === "alle" ? true : soort === "elk" ? k.diersoortId == null : k.diersoortId === soort || k.diersoortId == null),
  [keuzes, soort]);

  return (
    <section className="taak-keuzes" aria-label="Taken om te kiezen">
      <div className="taak-keuzes__kop">
        <div>
          <h4>Taken om te kiezen</h4>
          <p className="fawkes-muted">
            De taken die je zelf toevoegt bij een dier, per diersoort. Een taak met een handeling opent bij uitvoeren
            het invoerscherm (enting, wegen, notitie, ...) en is af zodra dat is opgeslagen.
          </p>
        </div>
        <Button onClick={() => setBewerk("nieuw")}>Nieuwe taak +</Button>
      </div>
      <div className="taak-keuzes__soorten" role="group" aria-label="Diersoort">
        <button type="button" aria-pressed={soort === "alle"} onClick={() => setSoort("alle")}>Alle</button>
        <button type="button" aria-pressed={soort === "elk"} onClick={() => setSoort("elk")}>Elke soort</button>
        {soorten.map((s) => (
          <button key={s.id} type="button" aria-pressed={soort === s.id} onClick={() => setSoort(s.id)}>{s.label}</button>
        ))}
      </div>
      {lijst.isError ? (
        <p className="fawkes-error">De taken konden niet worden geladen.</p>
      ) : zichtbaar.length === 0 ? (
        <p className="fawkes-muted">Nog geen taken om te kiezen.</p>
      ) : (
        <ul className="taak-keuzes__lijst">
          {zichtbaar.map((k) => (
            <li key={k.id} data-uit={k.actief ? undefined : ""}>
              <div className="taak-keuzes__tekst">
                <strong>{k.naam}</strong>
                <span className="fawkes-muted">
                  {[handelingLabel(k.handeling), k.diersoort ?? "Elke soort",
                    k.standaardTeam ? teams.find((x) => x.id === k.standaardTeam)?.label ?? k.standaardTeam : null,
                    k.actief ? null : "uitgezet"].filter(Boolean).join(" · ")}
                </span>
              </div>
              <Button variant="secondary" onClick={() => setBewerk(k)}>Bewerken</Button>
            </li>
          ))}
        </ul>
      )}
      {bewerk && (
        <KeuzeVenster keuze={bewerk === "nieuw" ? null : bewerk} soorten={soorten} teams={teams} prioriteiten={prioriteiten}
          wizards={wizards} onSluit={() => setBewerk(null)} onBewaard={() => { setBewerk(null); void lijst.refetch(); }} />
      )}
    </section>
  );
}

function KeuzeVenster({ keuze, soorten, teams, prioriteiten, wizards, onSluit, onBewaard }: {
  keuze: TaakKeuze | null;
  soorten: { id: number; label: string }[];
  teams: Optie[];
  prioriteiten: Optie[];
  wizards: Optie[];
  onSluit: () => void;
  onBewaard: () => void;
}) {
  const { mutate, isLoading } = useCustomMutation();
  const [f, setF] = useState({
    naam: keuze?.naam ?? "",
    omschrijving: keuze?.omschrijving ?? "",
    diersoortId: keuze?.diersoortId == null ? "" : String(keuze.diersoortId),
    handeling: keuze?.handeling ?? "VRIJ",
    standaardTeam: keuze?.standaardTeam ?? "",
    standaardPrioriteit: keuze?.standaardPrioriteit ?? "NORMAAL",
    wizardDefinitionId: keuze?.wizardDefinitionId ?? "",
    actief: keuze?.actief ?? true,
  });
  const [fout, setFout] = useState<string | null>(null);
  const zet = (n: Partial<typeof f>) => setF((x) => ({ ...x, ...n }));

  function bewaar() {
    if (!f.naam.trim()) { setFout("Geef de taak een naam."); return; }
    if (f.handeling === "WIZARD" && !f.wizardDefinitionId) { setFout("Kies de wizard die bij deze taak hoort."); return; }
    setFout(null);
    mutate({
      url: keuze ? `taak-keuzes/${keuze.id}` : "taak-keuzes",
      method: keuze ? "put" : "post",
      values: {
        naam: f.naam.trim(),
        omschrijving: f.omschrijving.trim() || null,
        diersoortId: f.diersoortId ? Number(f.diersoortId) : null,
        handeling: f.handeling,
        standaardTeam: f.standaardTeam || null,
        standaardPrioriteit: f.standaardPrioriteit,
        wizardDefinitionId: f.handeling === "WIZARD" ? f.wizardDefinitionId : null,
        actief: f.actief,
      },
    }, {
      onSuccess: onBewaard,
      onError: (e) => setFout((e as { message?: string })?.message || "Opslaan is niet gelukt."),
    });
  }

  return (
    <Modal open title={keuze ? "Taak bewerken" : "Nieuwe taak om te kiezen"} onClose={onSluit}
      footer={<>
        <Button variant="secondary" onClick={onSluit}>Annuleren</Button>
        <Button onClick={bewaar} disabled={isLoading}>Opslaan</Button>
      </>}>
      <div className="taak-keuzes__form">
        <label className="taak-keuzes__veld">
          <span className="taak-keuzes__label">Naam</span>
          <input className="fawkes-input" value={f.naam} maxLength={100} onChange={(e) => zet({ naam: e.target.value })}
            placeholder="Bijv. Enting kennelhoest" />
        </label>
        <label className="taak-keuzes__veld">
          <span className="taak-keuzes__label">Handeling bij uitvoeren</span>
          <select className="fawkes-input" value={f.handeling} onChange={(e) => zet({ handeling: e.target.value })}>
            {HANDELINGEN.map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
          </select>
        </label>
        {f.handeling === "WIZARD" && (
          <label className="taak-keuzes__veld">
            <span className="taak-keuzes__label">Wizard</span>
            <select className="fawkes-input" value={f.wizardDefinitionId} onChange={(e) => zet({ wizardDefinitionId: e.target.value })}>
              <option value="">Kies een wizard…</option>
              {wizards.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
            </select>
          </label>
        )}
        <label className="taak-keuzes__veld">
          <span className="taak-keuzes__label">Diersoort</span>
          <select className="fawkes-input" value={f.diersoortId} onChange={(e) => zet({ diersoortId: e.target.value })}>
            <option value="">Elke soort</option>
            {soorten.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className="taak-keuzes__veld">
          <span className="taak-keuzes__label">Standaard team</span>
          <select className="fawkes-input" value={f.standaardTeam} onChange={(e) => zet({ standaardTeam: e.target.value })}>
            <option value="">Geen team</option>
            {teams.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <label className="taak-keuzes__veld">
          <span className="taak-keuzes__label">Standaard prioriteit</span>
          <select className="fawkes-input" value={f.standaardPrioriteit} onChange={(e) => zet({ standaardPrioriteit: e.target.value })}>
            {prioriteiten.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <label className="taak-keuzes__veld taak-keuzes__breed">
          <span className="taak-keuzes__label">Omschrijving (optioneel)</span>
          <textarea className="fawkes-input" rows={2} maxLength={500} value={f.omschrijving}
            onChange={(e) => zet({ omschrijving: e.target.value })} />
        </label>
        {keuze && (
          <label className="taak-keuzes__aan">
            <input type="checkbox" checked={f.actief} onChange={(e) => zet({ actief: e.target.checked })} />
            <span>Actief (uitgezet is niet meer te kiezen)</span>
          </label>
        )}
        {fout && <p className="fawkes-error taak-keuzes__breed" role="alert">{fout}</p>}
      </div>
    </Modal>
  );
}
